import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Observable } from 'rxjs/internal/Observable';

import { MetaFeedConfig, HeroImageTemplate } from '@app/models/meta-feed-config';
// (MetaFeedStats re-declared here — kept alongside the service rather than in models/ since
// it's purely a response shape for the admin dashboard.)

export interface MetaFeedPreview {
    columns: string[];
    rows: { [key: string]: string }[];
}

export interface MetaFeedCacheState {
    warm: boolean;
    ageSeconds: number | null;
    expiresInSeconds: number | null;
}

export interface MetaFeedStats {
    activeJobCount: number;
    excludedCount: number;
    ttlSeconds: number;
    csvCache: MetaFeedCacheState;
    xmlCache: MetaFeedCacheState;
    hasDefaultTemplate: boolean;
}

@Injectable({ providedIn: 'root' })
export class MetaFeedService {
    private _baseUrl = '/api/meta-feed/';
    private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

    constructor(private httpClient: HttpClient) {}

    getConfig(): Observable<MetaFeedConfig> {
        return this.httpClient.get<MetaFeedConfig>(this._baseUrl + 'config');
    }

    saveConfig(cfg: MetaFeedConfig): Observable<MetaFeedConfig> {
        return this.httpClient.put<MetaFeedConfig>(this._baseUrl + 'config', JSON.stringify(cfg), this.headerOptions);
    }

    listTemplates(): Observable<HeroImageTemplate[]> {
        return this.httpClient.get<HeroImageTemplate[]>(this._baseUrl + 'templates');
    }

    getTemplate(id: string): Observable<HeroImageTemplate> {
        return this.httpClient.get<HeroImageTemplate>(this._baseUrl + 'templates/' + id);
    }

    createTemplate(tpl: HeroImageTemplate): Observable<HeroImageTemplate> {
        return this.httpClient.post<HeroImageTemplate>(this._baseUrl + 'templates', JSON.stringify(tpl), this.headerOptions);
    }

    updateTemplate(tpl: HeroImageTemplate): Observable<HeroImageTemplate> {
        return this.httpClient.put<HeroImageTemplate>(this._baseUrl + 'templates/' + tpl._id, JSON.stringify(tpl), this.headerOptions);
    }

    archiveTemplate(id: string): Observable<HeroImageTemplate> {
        return this.httpClient.delete<HeroImageTemplate>(this._baseUrl + 'templates/' + id);
    }

    getPreview(limit = 5): Observable<MetaFeedPreview> {
        return this.httpClient.get<MetaFeedPreview>(this._baseUrl + 'preview?limit=' + limit);
    }

    getStats(): Observable<MetaFeedStats> {
        return this.httpClient.get<MetaFeedStats>(this._baseUrl + 'stats');
    }

    invalidateCache(): Observable<{ ok: boolean }> {
        return this.httpClient.post<{ ok: boolean }>(this._baseUrl + 'invalidate-cache', {}, this.headerOptions);
    }

    regenerateHero(jobId: string): Observable<{ ok: boolean }> {
        return this.httpClient.post<{ ok: boolean }>(this._baseUrl + 'regenerate-hero/' + jobId, {}, this.headerOptions);
    }

    updateJobOverrides(jobId: string, overrides: any): Observable<any> {
        return this.httpClient.put(this._baseUrl + 'job-overrides/' + jobId, JSON.stringify(overrides), this.headerOptions);
    }
}
